import { forwardRef } from 'react';
import { Flex, ColorsV2, getToken, spaces, sizes } from '@electricjs/arc';
import styled from 'styled-components';
import { CenteredSpinner } from '@common';
import { CustomAssistantMessage } from './CustomAssistantMessage';
import { UserMessage } from './UserMessage';
import { LoadingMessage } from './LoadingMessage';
import { useAutoScroll } from './useAutoScroll';
import type { CopilotKitMessage } from './types';

const ErrorMessage = styled.div`
  padding: ${spaces.space_xs_rem} ${spaces.space_m_rem};
  margin-bottom: ${spaces.space_m_rem};
  background-color: ${getToken(ColorsV2.BACKGROUND_ALT)};
  border-radius: 0 8px 8px 8px;
  color: ${getToken(ColorsV2.ERROR)};
  font-size: ${sizes.size_16_rem};
  width: fit-content;
  max-width: 80%;
`;

const MessagesListContainer = styled(Flex)`
  width: 100%;
  flex-direction: column;
  padding: 0; /* Remove padding - parent CopilotKitChatContainer has padding */
  flex: 1;
  overflow-y: auto;
  background-color: ${getToken(ColorsV2.BACKGROUND_CONTENT)};
`;

type MessagesListProps = {
  messages: CopilotKitMessage[];
  isLoading: boolean;
  isInitializing?: boolean;
  runError?: string | null;
};

/**
 * MessagesList - Main component that renders all messages
 * Displays user and assistant messages with appropriate styling
 * Forwards ref to the scrollable container for ScrollToBottomButton
 */
export const MessagesList = forwardRef<HTMLDivElement, MessagesListProps>(
  ({ messages, isLoading, isInitializing = false, runError = null }, ref) => {
    // Auto-scroll behavior: scroll to bottom when messages arrive if user is near bottom
    useAutoScroll({
      messages,
      containerRef: ref,
      threshold: 100,
    });

    // Show CenteredSpinner during initial loading (fetching chat ID, initializing agent)
    if (isInitializing) {
      return (
        <MessagesListContainer ref={ref}>
          <CenteredSpinner mt="0" height="100%" alignItems="center" />
        </MessagesListContainer>
      );
    }

    // isStreaming must be based on position among rendered (user/assistant)
    // messages only — a trailing tool/system message in the raw array would
    // otherwise make the last visible assistant message look finished while
    // the run is still in flight.
    const lastRenderableId = [...messages]
      .reverse()
      .find(
        message => message.role === 'user' || message.role === 'assistant'
      )?.id;

    return (
      <MessagesListContainer ref={ref}>
        {messages.map(message => {
          // Only render user and assistant messages (filter out system messages)
          if (message.role === 'user') {
            return <UserMessage key={message.id} message={message} />;
          }

          if (message.role === 'assistant') {
            return (
              <CustomAssistantMessage
                key={message.id}
                message={message}
                isStreaming={isLoading && message.id === lastRenderableId}
              />
            );
          }

          // Skip rendering other message types (system, developer, tool)
          return null;
        })}

        {runError && <ErrorMessage>{runError}</ErrorMessage>}
        {isLoading && <LoadingMessage />}
      </MessagesListContainer>
    );
  }
);

MessagesList.displayName = 'MessagesList';
